import classNames from "classnames";

import type { ProductOptionPickerProps } from "..";
import {
  getActiveGroup,
  getValueLabel,
  makeGroupChangeHandler,
} from "../util/groups";

import Label from "./Label";
import OptionModal from "./OptionModal";
import OptionPicker from "./OptionPicker";
import RichGroupV2 from "./RichGroupV2";

export interface ClassicGroupPickerProps
  extends Omit<
    ProductOptionPickerProps,
    "type" | "values" | "useActiveGroupTitle"
  > {
  type?: ProductOptionPickerProps["type"];
  groups: NonNullable<ProductOptionPickerProps["groups"]>;
}

export default function ClassicGroupPickerV2({
  className,
  fullWidth,
  gridColumns,
  type,
  groups,
  label,
  name,
  onChange,
  selectedValue,
  isNested,
  modal,
  inModal,
}: ClassicGroupPickerProps) {
  const activeGroup = getActiveGroup(selectedValue, groups);
  if (!activeGroup) return null;

  const groupValues = groups.map((o) => ({
    label: o.label || o.name,
    value: o.name,
  }));
  const valueLabel = getValueLabel(selectedValue, activeGroup);

  const handleChange = makeGroupChangeHandler(name, groups, onChange);

  return (
    <div
      className={classNames(
        "flex flex-col",
        { "gap-2": !modal, "gap-4": modal },
        className
      )}
    >
      <div className="flex items-baseline justify-between gap-2">
        {isNested && label && (
          <Label>
            {label}:{" "}
            <span className="normal-case">
              {activeGroup.label ?? activeGroup.name}
            </span>
          </Label>
        )}

        {modal && (
          <OptionModal
            {...modal}
            richGroupPicker={RichGroupV2}
            groupPicker={ClassicGroupPickerV2}
            optionPicker={OptionPicker}
          />
        )}
      </div>

      <div className="flex flex-col gap-6">
        <OptionPicker
          fullWidth={fullWidth}
          gridColumns={gridColumns}
          mobileOrder={0}
          name="option-group-parent"
          onChange={handleChange}
          selectedValue={activeGroup.name}
          type={type}
          values={groupValues}
          inModal={inModal}
        />

        {activeGroup.values?.length && (
          <div className="flex flex-col gap-2">
            <div className="flex items-baseline justify-between gap-2">
              <p>
                <span className="uppercase">{activeGroup.title}: </span>
                {valueLabel}
              </p>

              {activeGroup?.modal && (
                <OptionModal
                  {...activeGroup.modal}
                  richGroupPicker={RichGroupV2}
                  groupPicker={ClassicGroupPickerV2}
                  optionPicker={OptionPicker}
                />
              )}
            </div>

            <OptionPicker
              fullWidth={activeGroup.fullWidth ?? true}
              gridColumns={activeGroup.gridColumns ?? 3}
              mobileOrder={1}
              name={activeGroup.name}
              onChange={handleChange}
              selectedValue={selectedValue}
              type={activeGroup.type}
              values={activeGroup.values}
              inModal={inModal}
            />
          </div>
        )}

        {activeGroup.groups?.length && (
          <div className="flex flex-col gap-4">
            <div className="flex items-baseline justify-between gap-2">
              <p>
                <span className="uppercase">{activeGroup.title}: </span>
                {selectedValue}
              </p>
              {activeGroup?.modal && (
                <OptionModal
                  {...activeGroup.modal}
                  richGroupPicker={RichGroupV2}
                  groupPicker={ClassicGroupPickerV2}
                  optionPicker={OptionPicker}
                />
              )}
            </div>

            <ClassicGroupPickerV2
              fullWidth={activeGroup.fullWidth ?? true}
              gridColumns={activeGroup.gridColumns ?? 3}
              groups={activeGroup.groups}
              label={activeGroup.name}
              mobileOrder={1}
              name={activeGroup.name}
              onChange={handleChange}
              selectedValue={selectedValue}
              type={activeGroup.type}
              modal={null}
              inModal={inModal}
            />
          </div>
        )}
      </div>
    </div>
  );
}
